import { useEffect, useSyncExternalStore } from "react";

/**
 * Tells the app-level toaster that a mounted public page is forced light.
 *
 * `RouteAwareToaster` is a sibling above the routes, so it cannot read the page's
 * `ForcedLightContext`; and a forced-light wrapper cannot reach Sonner, which takes its theme from
 * next-themes. A page registers here while mounted, and the toaster follows. A count (not a
 * boolean) so an unmounting page cannot clear a registration a newly mounted one just made.
 */
let forcedCount = 0;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePublicForceLightActive(): boolean {
  return useSyncExternalStore(subscribe, () => forcedCount > 0, () => false);
}

/** Registers the calling page as forced light for as long as `enabled` and mounted. */
export function useRegisterPublicForceLight(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    forcedCount += 1;
    emit();
    return () => {
      forcedCount -= 1;
      emit();
    };
  }, [enabled]);
}
